import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCode as FaCodeBase,
  FaChartBar as FaChartBarBase,
  FaBrain as FaBrainBase,
  FaArrowRight as FaArrowRightBase,
} from 'react-icons/fa';

// Fix for TS2786: Cast icons to React.ElementType
const FaCode = FaCodeBase as React.ElementType;
const FaChartBar = FaChartBarBase as React.ElementType;
const FaBrain = FaBrainBase as React.ElementType;
const FaArrowRight = FaArrowRightBase as React.ElementType;

type Category = 'All' | 'Data Science' | 'AI/ML' | 'Web Dev';

interface Metric {
  value: string;
  label: string;
}

interface Project {
  id: string;
  title: string;
  category: Exclude<Category, 'All'>;
  // Optional real screenshot. When absent, a branded gradient cover is rendered.
  image?: string;
  problem: string;
  solution: string;
  result: string;
  metrics: Metric[];
  tech: string[];
  link: string;
  featured?: boolean;
}

// Per-category visual identity used for the generated cover art (no external images).
const categoryStyles: Record<Exclude<Category, 'All'>, { gradient: string; Icon: React.ElementType }> = {
  'AI/ML': { gradient: 'from-purple-600 to-indigo-700', Icon: FaBrain },
  'Data Science': { gradient: 'from-emerald-600 to-teal-700', Icon: FaChartBar },
  'Web Dev': { gradient: 'from-blue-600 to-cyan-700', Icon: FaCode },
};

// Branded placeholder cover — renders when a project has no real screenshot yet.
const ProjectCover: React.FC<{ project: Project; large?: boolean }> = ({ project, large }) => {
  const { gradient, Icon } = categoryStyles[project.category];
  return (
    <div className={`relative w-full h-full bg-gradient-to-br ${gradient} flex flex-col items-center justify-center overflow-hidden`}>
      {/* subtle grid texture */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <Icon className="text-white/90 mb-3" size={large ? 64 : 40} />
      <span className={`text-white font-bold text-center px-6 ${large ? 'text-2xl' : 'text-base'}`}>
        {project.title}
      </span>
      <span className="mt-2 text-white/70 text-xs uppercase tracking-widest">{project.category}</span>
    </div>
  );
};

const projects: Project[] = [
  {
    id: '1',
    title: 'Enterprise RAG Knowledge Agent',
    category: 'AI/ML',
    // image: '/portfolio/rag-agent.png',
    problem: 'Staff wasted hours searching scattered internal documents with no reliable answers.',
    solution: 'Built a context-aware AI assistant over a vector-search knowledge base with source citations.',
    result: 'Instant, sourced answers from internal data — turning hours of searching into seconds.',
    metrics: [
      { value: '90%', label: 'faster lookups' },
      { value: '10k+', label: 'docs indexed' },
      { value: '<1s', label: 'avg. response' },
    ],
    tech: ['LangChain', 'Claude 3.5', 'Gemini 1.5', 'Pinecone'],
    link: '/portfolio/rag-agent',
    featured: true,
  },
  {
    id: '2',
    title: 'Ticketing Incident Dashboard',
    category: 'Web Dev',
    // image: '/portfolio/ticketing-dashboard.png',
    problem: 'Support team had no real-time view of incidents and routinely missed SLAs.',
    solution: 'Built a live dashboard with WebSocket updates and automated SLA tracking.',
    result: 'Full real-time visibility and measurably faster incident response.',
    metrics: [
      { value: 'Real-time', label: 'updates' },
      { value: '100%', label: 'SLA visibility' },
    ],
    tech: ['React', 'Node.js', 'Socket.io'],
    link: '/portfolio/ticketing-dashboard',
  },
  {
    id: '3',
    title: 'Sales Analytics Platform',
    category: 'Data Science',
    // image: '/portfolio/sales-dashboard.png',
    problem: 'Revenue data was scattered across channels with no forecasting.',
    solution: 'Aggregated multi-channel streams and layered in predictive forecasting.',
    result: 'A single source of truth for revenue with forward-looking projections.',
    metrics: [
      { value: '6', label: 'channels unified' },
      { value: '12mo', label: 'forecast horizon' },
    ],
    tech: ['Python', 'Pandas', 'Chart.js'],
    link: '/portfolio/sales-dashboard',
  },
  {
    id: '4',
    title: 'Stock Market Predictor',
    category: 'Data Science',
    // image: '/portfolio/stock-market-tracker.png',
    problem: 'Needed a way to spot price trends from noisy, high-frequency market data.',
    solution: 'Trained LSTM neural networks behind a real-time financial dashboard.',
    result: 'Live trend predictions surfaced in an easy-to-read interface.',
    metrics: [
      { value: 'LSTM', label: 'deep model' },
      { value: 'Live', label: 'market feed' },
    ],
    tech: ['TensorFlow', 'React', 'FastAPI'],
    link: '/portfolio/stock-market-tracker',
  },
  {
    id: '5',
    title: 'Customer Segmentation Engine',
    category: 'Data Science',
    // image: '/portfolio/customer-segmentation.png',
    problem: 'Marketing treated all customers the same, wasting spend on low-value cohorts.',
    solution: 'Ran RFM analysis and K-Means clustering to surface high-value segments.',
    result: 'Targeted campaigns focused budget on the most valuable customers.',
    metrics: [
      { value: 'RFM', label: 'analysis' },
      { value: 'K-Means', label: 'clustering' },
    ],
    tech: ['Scikit-learn', 'Python', 'D3.js'],
    link: '/portfolio/customer-segmentation',
  },
  {
    id: '6',
    title: 'YouTube Sentiment Analyzer',
    category: 'AI/ML',
    // image: '/portfolio/youtube-sentiment-analyzer.png',
    problem: 'Creators had no quick read on how audiences felt about their content.',
    solution: 'Built an NLP pipeline to extract sentiment and topic clusters from comments.',
    result: 'At-a-glance audience sentiment and trending themes per video.',
    metrics: [
      { value: 'BERT', label: 'NLP model' },
      { value: 'Real-time', label: 'analysis' },
    ],
    tech: ['BERT', 'NLTK', 'YouTube API'],
    link: '/portfolio/youtube-sentiment-analyzer',
  },
  {
    id: '7',
    title: 'Smart Weather Forecast',
    category: 'Web Dev',
    // image: '/portfolio/weather-forecast-app.png',
    problem: 'Generic forecasts missed hyper-local micro-climate conditions.',
    solution: 'Combined location data with historical patterns for localized predictions.',
    result: 'More accurate, place-specific weather at a glance.',
    metrics: [
      { value: 'Hyper-local', label: 'accuracy' },
      { value: 'Map-based', label: 'UI' },
    ],
    tech: ['React', 'OpenWeather', 'Leaflet'],
    link: '/portfolio/weather-forecast-app',
  },
];

const FeaturedProject: React.FC<{ project: Project }> = ({ project }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="mb-16 rounded-2xl overflow-hidden shadow-2xl border border-primary bg-primary grid lg:grid-cols-2"
  >
    <div className="relative overflow-hidden h-64 lg:h-auto group">
      {project.image ? (
        <img src={project.image} alt={project.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
      ) : (
        <ProjectCover project={project} large />
      )}
      <span className="absolute top-4 left-4 accent-bg text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg">
        ★ Featured Project
      </span>
    </div>
    <div className="p-8 lg:p-12 flex flex-col justify-center">
      <span className="text-xs font-bold uppercase tracking-wider text-accent mb-2">{project.category}</span>
      <h3 className="text-3xl font-bold text-primary mb-4">{project.title}</h3>
      <div className="space-y-3 mb-6 text-sm">
        <p className="text-secondary"><strong className="text-primary">Problem: </strong>{project.problem}</p>
        <p className="text-secondary"><strong className="text-primary">Solution: </strong>{project.solution}</p>
        <p className="text-secondary"><strong className="text-accent">Result: </strong>{project.result}</p>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {project.metrics.map((m) => (
          <div key={m.label} className="text-center p-3 bg-secondary rounded-lg border border-primary/50">
            <div className="text-2xl font-bold text-accent">{m.value}</div>
            <div className="text-xs text-secondary uppercase tracking-wide">{m.label}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {project.tech.map((t) => (
          <span key={t} className="px-3 py-1 bg-secondary text-primary text-xs rounded-md border border-primary/50">{t}</span>
        ))}
      </div>
      <Link to={project.link} className="accent-bg text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition shadow-lg inline-flex items-center justify-center gap-2 self-start">
        View Full Case Study <FaArrowRight size={12} />
      </Link>
    </div>
  </motion.div>
);

const Portfolio: React.FC = () => {
  const [filter, setFilter] = useState<Category>('All');

  const featured = projects.find((p) => p.featured);
  const gridProjects = projects.filter((p) => !p.featured);

  const filteredProjects = gridProjects.filter(
    (p) => filter === 'All' || p.category === filter
  );

  return (
    <section className="py-24 min-h-screen bg-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block mb-4 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold"
          >
            Portfolio
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-primary mb-4"
          >
            Work That Ships Results
          </motion.h2>
          <p className="text-xl text-secondary max-w-2xl mx-auto">
            Real problems, solved with working software. Each project shows the challenge, what I built, and the measurable outcome.
          </p>
        </div>

        {/* Featured */}
        {featured && <FeaturedProject project={featured} />}

        {/* Filter Tabs */}
        <div className="flex justify-center mb-12 flex-wrap gap-4">
          {(['All', 'Data Science', 'AI/ML', 'Web Dev'] as Category[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                filter === cat
                  ? 'accent-bg text-white shadow-lg scale-105'
                  : 'bg-primary text-secondary border border-primary hover:border-accent'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredProjects.map((project) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                key={project.id}
                className="bg-primary rounded-xl overflow-hidden shadow-lg border border-primary hover:shadow-2xl transition-shadow duration-300 group flex flex-col"
              >
                <div className="relative overflow-hidden h-48">
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full transform group-hover:scale-105 transition-transform duration-500">
                      <ProjectCover project={project} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Link
                      to={project.link}
                      className="bg-white text-black px-6 py-2 rounded-full font-bold transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
                    >
                      View Case Study
                    </Link>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      {project.category}
                    </span>
                    <div className="flex gap-2">
                      {project.category === 'AI/ML' && <FaBrain className="text-secondary" />}
                      {project.category === 'Web Dev' && <FaCode className="text-secondary" />}
                      {project.category === 'Data Science' && <FaChartBar className="text-secondary" />}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors">
                    {project.title}
                  </h3>

                  <div className="space-y-2 mb-4 text-sm flex-1">
                    <p className="text-secondary">
                      <strong className="text-primary">Problem: </strong>{project.problem}
                    </p>
                    <p className="text-secondary">
                      <strong className="text-primary">Solution: </strong>{project.solution}
                    </p>
                    <p className="text-secondary">
                      <strong className="text-accent">Result: </strong>{project.result}
                    </p>
                  </div>

                  {/* Metrics */}
                  <div className="flex gap-3 mb-4">
                    {project.metrics.map((m) => (
                      <div key={m.label} className="flex-1 text-center p-2 bg-secondary rounded-lg border border-primary/50">
                        <div className="text-sm font-bold text-accent">{m.value}</div>
                        <div className="text-[10px] text-secondary uppercase tracking-wide">{m.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((t) => (
                      <span key={t} className="px-2 py-1 bg-secondary text-primary text-xs rounded-md border border-primary/50">
                        {t}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={project.link}
                    className="inline-flex items-center gap-2 text-accent font-semibold text-sm hover:gap-3 transition-all"
                  >
                    View Case Study <FaArrowRight size={12} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* CTA */}
        <div className="text-center mt-24 p-12 bg-primary rounded-2xl border border-primary shadow-lg">
          <h3 className="text-3xl font-bold text-primary mb-3">Have something like this in mind?</h3>
          <p className="text-secondary mb-8 text-lg max-w-2xl mx-auto">
            Whether it's a quick tool or a full product, I'll help you scope it and build it. Reach out — the first conversation is free and there's no obligation.
          </p>
          <Link to="/#contact" className="accent-bg text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition shadow-lg inline-block">
            Start Your Project
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;